import { tracked } from '@glimmer/tracking';
import RSVP from 'rsvp';
import Service, { service } from '@ember/service';
import Store from '@ember-data/store';
import AllFundsAbilityModel from 'donor-elf-web/models/all-funds/ability';
import AbilityModel from 'donor-elf-web/models/ability';

export default class AbilitiesService extends Service {
  @service declare session: any;
  @service declare store: Store;

  @tracked abilities: { [ability: string]: boolean } = {};
  @tracked coachingAbilities: { [ability: string]: boolean } = {};

  hasAbility(abilityName: string): boolean {
    return this.abilities[abilityName] || false;
  }

  noAbility(abilityName: string): boolean {
    return !this.hasAbility(abilityName);
  }

  hasCoachingAbility(abilityName: string): boolean {
    return this.coachingAbilities[abilityName] || false;
  }

  noCoachingAbility(abilityName: string): boolean {
    return !this.hasCoachingAbility(abilityName);
  }

  _loadForFundPromise: { [fundId: string]: Promise<AbilityModel> } = {};
  loadForFund(fundId: string) {
    if (!this.session.isAuthenticated) {
      return Promise.resolve();
    }
    if (this._loadForFundPromise[fundId] != null) {
      return this._loadForFundPromise[fundId].then((abilityModel) => {
        // always update this.abilities in case the user came from another fund
        this.abilities = abilityModel.abilities;
        return undefined;
      });
    }
    this._loadForFundPromise[fundId] = this.store
      .query('ability', {})
      .then((records) => {
        let abilityModel = records.slice()[0];
        this.abilities = abilityModel.abilities;
        return abilityModel;
      });
    return this._loadForFundPromise[fundId].then(() => {
      return undefined;
    });
  }

  _loadAllFundsPromise?: Promise<AllFundsAbilityModel>;
  loadForAllFunds(): Promise<void> {
    if (!this.session.isAuthenticated) {
      return Promise.resolve();
    }
    if (this._loadAllFundsPromise) {
      return this._loadAllFundsPromise.then((abilityModel) => {
        // always update this.abilities in case the user came from a single fund
        this.abilities = abilityModel.abilities;
        return undefined;
      });
    }
    this._loadAllFundsPromise = this.store
      .query('all-funds/ability', {})
      .then((records) => {
        let abilityModel = records.slice()[0];
        this.abilities = abilityModel.abilities;
        return abilityModel;
      });
    return this._loadAllFundsPromise.then(() => {
      return undefined;
    });
  }

  // { individualId: { promise, record } }
  // individualId of -1 represents generic abilities not tied to any specific individual
  _loadCoachingAbilitiesCache: {
    [individualId: number]: { promise: Promise<void>; record: any };
  } = {};

  loadCoachingAbilities({ individualId }: { individualId?: number } = {}) {
    if (!this.session.isAuthenticated) {
      return RSVP.resolve();
    }
    if (individualId == null) {
      individualId = -1;
    }
    let cache = this._loadCoachingAbilitiesCache[individualId];
    if (cache != null) {
      this._updateCoachingAbilitiesFromRecord(cache.record);
      return cache.promise;
    }

    // @ts-ignore
    cache = {};
    cache.promise = this.store
      .findRecord('coaching/coaching-ability', individualId)
      .then((record) => {
        cache.record = record;
        this._updateCoachingAbilitiesFromRecord(record);
      });
    this._loadCoachingAbilitiesCache[individualId] = cache;

    return cache.promise;
  }

  _updateCoachingAbilitiesFromRecord(record: any) {
    this.coachingAbilities = record.abilities || {};
  }

  unload() {
    this.abilities = {};
    this.coachingAbilities = {};
    this._loadPromise = undefined;
    this._loadCoachingAbilitiesCache = {};
  }
}
